import React, { useEffect, useState } from 'react';
import { userService } from '../services/user.service';
import SelectDropdown from './select';
import { lookupService } from '../services/lookups.service';
import MultiSelectDropdown from './multi-select';
import { Role } from '../services/user.service.types';
import Dialog from './Dialog';
import { AlertCircleIcon } from 'lucide-react';
import { authService } from '../services/auth.service';
import StatusPill from './StatusPill';

interface UserDetailsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  isEditMode: boolean;
  onEditClick: () => void;
  onSaveEdit: () => void;
  editFormData: any;
  onEditInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> |
    { target: { name: string; value: any } }
  ) => void;
  onShowBlockDialog: () => void;
}

const EMAIL_REGEX_STRICT = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const UserDetailsOverlay: React.FC<UserDetailsOverlayProps> = ({
  isOpen,
  onClose,
  user,
  isEditMode,
  onEditClick,
  onSaveEdit,
  editFormData,
  onEditInputChange,
  onShowBlockDialog,
}): JSX.Element => {
  // Canadian phone number regex
  // Accepts formats: +1XXXXXXXXXX, 1XXXXXXXXXX, XXXXXXXXXX, XXX-XXX-XXXX, (XXX) XXX-XXXX, +1 (XXX) XXX-XXXX
  const PHONE_REGEX = /^(\+?1)?[-. ]?\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/;

  const [roles, setRoles] = useState<Role[]>([]);
  const [, setPendingStatusChange] = useState<boolean | null>(null);
  const [provinces, setProvinces] = useState<any[]>([]);
  const [locals, setLocals] = useState<any[]>([]);
  const [filteredLocals, setFilteredLocals] = useState<any[]>([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasLocalBeenModified, setHasLocalBeenModified] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [selectedLocals, setSelectedLocals] = useState<string[]>([]);
  const [showUpdateConfirmDialog, setShowUpdateConfirmDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [originalFormData, setOriginalFormData] = useState<any>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  // Add useEffect to get current user
  useEffect(() => {
    const getCurrentUser = () => {
      const user = authService.getUser();
      setCurrentUser(user);
    };
    getCurrentUser();
  }, []);

  // Add useEffect to handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is restored when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Add beforeunload event listener to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEditMode && hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    if (isEditMode && hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEditMode, hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesData, provincesData, localsData] = await Promise.all([
          userService.getRoles(),
          lookupService.getProvinces(),
          lookupService.getLocals()
        ]);
        setRoles(rolesData);
        setProvinces(provincesData);
        setLocals(localsData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isEditMode && user?.local) {
      const existingLocals = user.local.map((local: any) => String(local.id));
      setSelectedLocals(existingLocals);
      onEditInputChange({
        target: {
          name: 'local',
          value: existingLocals
        }
      });
    }
  }, [isEditMode, user?.local]);

  // Store original form data when entering edit mode
  useEffect(() => {
    if (isEditMode && editFormData && !originalFormData) {
      // Only set original data once when entering edit mode
      setOriginalFormData(JSON.parse(JSON.stringify(editFormData)));
      setHasUnsavedChanges(false);
    } else if (!isEditMode) {
      // Reset when exiting edit mode
      setOriginalFormData(null);
      setHasUnsavedChanges(false);
    }
  }, [isEditMode, editFormData, originalFormData]);

  // Check for unsaved changes whenever form data changes
  useEffect(() => {
    if (isEditMode && originalFormData && editFormData) {
      // Use a more robust comparison that handles deep object differences
      const originalStr = JSON.stringify(originalFormData, Object.keys(originalFormData).sort());
      const currentStr = JSON.stringify(editFormData, Object.keys(editFormData).sort());
      const hasChanges = originalStr !== currentStr;

      setHasUnsavedChanges(hasChanges);
    }
  }, [editFormData, originalFormData, isEditMode]);

  useEffect(() => {
    if (editFormData?.region && editFormData.region.length > 0) {
      // Filter locals based on all selected regions
      const allRegionLocals = locals.filter(local => 
        editFormData.region.some((province: any) => local.province?.id === province.id)
      );
      setFilteredLocals(allRegionLocals);

      if (hasLocalBeenModified) {
        setSelectedLocals([]);
        onEditInputChange({
          target: {
            name: 'local',
            value: []
          }
        });
      }

      // Only show local validation error if role is organizer and any province has valid locals
      if (editFormData?.role?.roleName === 'organizer') {
        const hasValidLocalsForAnyProvince = allRegionLocals.some(local => 
          local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A'
        );
        
        if (hasValidLocalsForAnyProvince) {
          setValidationErrors(prev => ({
            ...prev,
            local: 'Please select at least one local area'
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev,
            local: '' // Clear local error if no valid locals available
          }));
        }
      }
    } else {
      setFilteredLocals([]);
    }
  }, [editFormData?.region, locals, hasLocalBeenModified, editFormData?.role?.roleName]);

  useEffect(() => {
    const validateForm = () => {
      const errors: { [key: string]: string } = {};

      // Name validation
      if (!editFormData?.name?.trim()) {
        errors.name = 'Name is required';
      } else if (editFormData.name.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters';
      } else if (editFormData.name.trim().length > 50) {
        errors.name = 'Name must not exceed 50 characters';
      } else if (!/^[a-zA-Z\s'-]*$/.test(editFormData.name.trim())) {
        errors.name = 'Name should only contain letters, spaces, apostrophes, and hyphens';
      }

      // Email validation
      if (!editFormData?.email?.trim()) {
        errors.email = 'Email is required';
      } else {
        if (!EMAIL_REGEX_STRICT.test(editFormData.email)) {
          errors.email = 'Please enter a valid email address';
        }
      }

      // Mobile number validation
      if (!editFormData?.mobileNumber?.trim()) {
        errors.mobileNumber = 'Mobile number is required';
      } else {
        if (!PHONE_REGEX.test(editFormData.mobileNumber)) {
          errors.mobileNumber = 'Please enter a valid Canadian phone number';
        }
      }

      // Region and Local validation only for organizer role
      if (editFormData?.role?.roleName === 'organizer') {
        if (!editFormData?.region || editFormData.region.length === 0) {
          errors.region = 'Please select at least one province';
        }
        
        // Only require locals if the province has valid locals available
        const hasValidLocalsForProvince = filteredLocals.some(local => 
          local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A'
        );
        
        if (hasValidLocalsForProvince && (!editFormData?.local || editFormData.local.length === 0)) {
          errors.local = 'Please select at least one local area';
        }
      }

      setValidationErrors(errors);
      const isValid = Object.keys(errors).length === 0;
      setIsFormValid(isValid);
    };

    if (isEditMode) {
      validateForm();
    } else {
      setValidationErrors({});
    }
  }, [editFormData, isEditMode]);

  const handleLocalStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.checked;

    // Prevent status change if user is editing their own profile
    if (currentUser?.id === user?.id) {
      return;
    }

    if (editFormData?.status === 'Active' && !newStatus) {
      onShowBlockDialog();
    } else {
      if (isEditMode && onEditInputChange) {
        onEditInputChange({
          target: {
            name: 'status',
            value: newStatus ? 'Active' : 'Inactive'
          }
        });
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setPendingStatusChange(null);
    }
  }, [isOpen]);

  useEffect(() => {
  }, [editFormData, provinces]);

  const handleSaveClick = () => {
    if (isFormValid) {
      setShowUpdateConfirmDialog(true);
    }
  };

  const handleConfirmUpdate = () => {
    setShowUpdateConfirmDialog(false);
    onSaveEdit();
  };

  const handleCancelUpdate = () => {
    setShowUpdateConfirmDialog(false);
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    console.log('handleClose called:', { isEditMode, hasUnsavedChanges });
    if (isEditMode && hasUnsavedChanges) {
      console.log('Showing unsaved changes dialog');
      setShowUnsavedChangesDialog(true);
    } else {
      console.log('Closing without warning');
      onClose();
    }
  };

  // Handle close without saving
  const handleCloseWithoutSaving = () => {
    setShowUnsavedChangesDialog(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  // Handle cancel unsaved changes dialog
  const handleCancelUnsavedChanges = () => {
    setShowUnsavedChangesDialog(false);
  };

  // Add a new function to handle mobile number formatting
  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    let cleaned = value.replace(/\D/g, '');

    // Ensure the number starts with 1 if it's 11 digits
    if (cleaned.length === 11 && cleaned.charAt(0) === '1') {
      cleaned = cleaned.substring(1);
    }

    // Format the number as (XXX) XXX-XXXX
    let formatted = cleaned;
    if (cleaned.length > 0) {
      if (cleaned.length <= 3) {
        formatted = `(${cleaned}`;
      } else if (cleaned.length <= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
    }
    return formatted;
  };

  // Update the onEditInputChange handler to format phone numbers
  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> |
    { target: { name: string; value: any } }
  ) => {
    const { name, value } = e.target;

    if (name === 'mobileNumber') {
      const formattedNumber = formatPhoneNumber(value);
      onEditInputChange({
        target: {
          name: 'mobileNumber',
          value: formattedNumber
        }
      });

      // Clear error when input becomes valid
      if (PHONE_REGEX.test(formattedNumber)) {
        setValidationErrors(prev => ({ ...prev, mobileNumber: '' }));
      }
    } else if (name === 'email') {
      // Convert email to lowercase
      const lowercaseEmail = value.toLowerCase();
      onEditInputChange({
        target: {
          name: 'email',
          value: lowercaseEmail
        }
      });

      // Clear validation error when valid email is provided
      if (EMAIL_REGEX_STRICT.test(lowercaseEmail)) {
        setValidationErrors(prev => ({ ...prev, email: '' }));
      }
    } else {
      onEditInputChange({
        target: {
          name,
          value
        }
      });

      // Clear validation errors for other fields when valid input is provided
      if (name === 'name') {
        if (value.trim() && value.trim().length >= 2 && /^[a-zA-Z\s'-]*$/.test(value.trim())) {
          setValidationErrors(prev => ({ ...prev, name: '' }));
        }
      }
    }
  };

  const getRoleDisplayName = (role: { roleName: string }): string => {
    switch (role?.roleName) {
      case 'organizer':
        return 'Organizer';
      case 'interRepre':
        return 'International Representative';
      case 'admin':
        return 'Admin';
      default:
        return role?.roleName || 'Unknown Role';
    }
  };

  const mapRoleOption = (role: Role) => ({
    id: role.id,
    name: getRoleDisplayName({ roleName: role.roleName })
  });

  return (
    <>
      <div
        className={`fixed inset-0 bg-[#A42823] transition-all duration-300 ease-in-out ${isOpen ? 'bg-black bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
          } z-50`}
        onClick={e => {
          // Only close if the click is on the overlay itself, not the side panel
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
        data-testid="user-details-overlay-bg"
      >
        <div
          className={`fixed right-0 bg-white h-screen w-full md:w-[500px] shadow-lg transition-all duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
            } flex flex-col`}
        >
          <div className="flex justify-between items-center p-4 border-b border-border">
            <h2 className="text-[24px] font-heading">
              {isEditMode ? 'Edit User' : user?.name}
            </h2>
            <div className="flex items-center gap-2">
              {!isEditMode && (
                <button
                  className="btn-secondary text-small"
                  onClick={onEditClick}
                  id="edit-user-button"
                >
                  Edit User
                </button>
              )}
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full"
                id="close-user-overlay-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="overflow-y-auto">
            <div className="flex-1 overflow-visible">
              <div className="p-6 space-y-6 pb-24">
                <div className="grid grid-cols-1 gap-4">
                  {/* Show editable fields in edit mode */}
                  {isEditMode ? (
                    <>
                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          placeholder="Enter full name"
                          value={editFormData?.name || ''}
                          onChange={handleEditInputChange}
                          className={`w-full px-3 py-2 text-[16px] border ${validationErrors.name ? 'border-red-500' : 'border-border'
                            } rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                          id="user-name-input"
                        />
                        {validationErrors.name && (
                          <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">
                          Email ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter email id"
                          value={editFormData?.email || ''}
                          onChange={handleEditInputChange}
                          className={`w-full px-3 py-2 text-[16px] border ${validationErrors.email ? 'border-red-500' : 'border-border'
                            } rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                          id="user-email-input"
                        />
                        {validationErrors.email && (
                          <p className="mt-1 text-sm text-red-500">{validationErrors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">
                          Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="mobileNumber"
                          placeholder="Enter mobile number"
                          value={editFormData?.mobileNumber || ''}
                          onChange={handleEditInputChange}
                          className={`w-full px-3 py-2 text-[16px] border ${validationErrors.mobileNumber ? 'border-red-500' : 'border-border'
                            } rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                          id="user-mobile-input"
                        />
                        {validationErrors.mobileNumber && (
                          <p className="mt-1 text-sm text-red-500">{validationErrors.mobileNumber}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">
                          Role <span className="text-red-500">*</span>
                        </label>
                        <SelectDropdown
                          testId="role-select"
                          options={roles
                            .filter(role => role.id !== '1')
                            .map(mapRoleOption)}
                          selectedValue={editFormData?.role?.id || ''}
                          onSelect={(value) => {
                            const selectedRole = roles.find(r => r.id === value);

                            // Clear region and local if International Representative or Admin is selected
                            if (selectedRole?.roleName === 'interRepre' || selectedRole?.roleName === 'admin') {
                              handleEditInputChange({
                                target: {
                                  name: 'region',
                                  value: null
                                }
                              });
                              handleEditInputChange({
                                target: {
                                  name: 'local',
                                  value: []
                                }
                              });
                              setSelectedLocals([]);
                              setFilteredLocals([]);

                              // Clear validation errors for region and local
                              setValidationErrors(prev => ({
                                ...prev,
                                region: '',
                                local: ''
                              }));
                            }

                            // Clear region and local if switching to Organizer role
                            if (selectedRole?.roleName === 'organizer') {
                              handleEditInputChange({
                                target: {
                                  name: 'region',
                                  value: null
                                }
                              });
                              handleEditInputChange({
                                target: {
                                  name: 'local',
                                  value: []
                                }
                              });
                              setSelectedLocals([]);
                              setFilteredLocals([]);
                            }

                            // Set the role
                            handleEditInputChange({
                              target: {
                                name: 'role',
                                value: selectedRole ? {
                                  id: value,
                                  roleName: selectedRole.roleName
                                } : null
                              }
                            });

                            // Clear validation errors for both cases
                            setValidationErrors(prev => ({
                              ...prev,
                              region: '',
                              local: ''
                            }));
                          }}
                          getValue={(option) => option.id}
                          getDisplayText={(option) => option.name}
                          placeholder="Select a role"
                        />
                      </div>

                      {editFormData?.role?.roleName === 'organizer' && (
                        <>
                          <div>
                            <label className="block text-[14px] font-[500] text-[#767A81] mb-1">
                              Province(s) <span className="text-red-500">*</span>
                            </label>
                            <MultiSelectDropdown
                              testId="select-province"
                              options={provinces.map(province => ({
                                id: province.id,
                                name: province.name
                              }))}
                              onSelect={(selectedProvinces) => {
                                handleEditInputChange({
                                  target: {
                                    name: 'region',
                                    value: selectedProvinces
                                  }
                                });

                                // Set hasLocalBeenModified to true when region changes
                                setHasLocalBeenModified(true);

                                // Clear validation error when regions are selected
                                if (selectedProvinces.length > 0) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    region: ''
                                  }));
                                }

                                // Filter locals based on all selected regions
                                const allRegionLocals = locals.filter(local => 
                                  selectedProvinces.some((province: any) => local.province?.id === province)
                                );
                                setFilteredLocals(allRegionLocals);

                                // Clear existing local selections when regions change
                                setSelectedLocals([]);
                                handleEditInputChange({
                                  target: {
                                    name: 'local',
                                    value: []
                                  }
                                });
                              }}
                              getValue={(province) => String(province.id)}
                              getDisplayText={(province) => province.name}
                              placeholder="Select province(s)"
                              selectedValues={editFormData?.region || []}
                            />
                            {validationErrors.region && (
                              <p className="mt-1 text-sm text-red-500">{validationErrors.region}</p>
                            )}
                          </div>

                          {editFormData?.region && editFormData.region.length > 0 && filteredLocals.some(local => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A') && (
                            <div>
                              <label className="block text-[14px] font-[500] text-[#767A81] mb-1">
                                Local Unions <span className="text-red-500">*</span>
                              </label>
                              <MultiSelectDropdown
                                testId="select-locals"
                                options={filteredLocals.filter(local => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A')}
                                selectedValues={selectedLocals}
                                onSelect={(selected) => {
                                  setSelectedLocals(selected);
                                  handleEditInputChange({
                                    target: {
                                      name: 'local',
                                      value: selected
                                    }
                                  });
                                  // Set hasLocalBeenModified when locals are selected
                                  setHasLocalBeenModified(true);
                                  // Clear validation error when locals are selected
                                  if (selected.length > 0) {
                                    setValidationErrors(prev => ({
                                      ...prev,
                                      local: ''
                                    }));
                                  } else {
                                    setValidationErrors(prev => ({
                                      ...prev,
                                      local: 'Please select at least one local area'
                                    }));
                                  }
                                }}
                                getValue={(local) => String(local.id)}
                                getDisplayText={(local) => `${local.code} - Local ${local.unionNumber}`}
                                placeholder="Select local unions"
                                isValidOption={(local) => !!local.code && !!local.unionNumber}
                              />
                              {validationErrors.local && (
                                <p className="mt-1 text-sm text-red-500">{validationErrors.local}</p>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">
                          Status
                        </label>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="relative inline-flex items-center">
                            <label className={`relative flex items-center ${currentUser?.id === user?.id ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                              <input
                                type="checkbox"
                                checked={editFormData?.status === 'Active'}
                                onChange={handleLocalStatusChange}
                                className="sr-only peer"
                                disabled={currentUser?.id === user?.id}
                                id="user-status-toggle"
                              />
                              <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white ${currentUser?.id === user?.id ? 'opacity-50' : 'cursor-pointer'}`}></div>
                            </label>
                          </div>
                          <span className="text-sm font-medium">
                            {editFormData?.status}
                            {currentUser?.id === user?.id && (
                              <span className="ml-2 text-xs text-gray-500">(Cannot change own status)</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Name</label>
                        <div className="text-[16px] font-[500] text-[#000000]">{user?.name}</div>
                      </div>

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Email ID</label>
                        <div className="text-[16px] font-[500] text-[#000000]">{user?.email}</div>
                      </div>

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Mobile Number</label>
                        <div className="text-[16px] font-[500] text-[#000000]">{user?.mobileNumber}</div>
                      </div>

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Status</label>
                        <div>
                          {user?.status ? (
                            <StatusPill status={user.status.toLowerCase()} />
                          ) : '-'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Role</label>
                        <div className="text-[16px] font-[500] text-[#000000]">
                          {getRoleDisplayName(user?.role)}
                        </div>
                      </div>

                      {user?.role?.roleName === 'organizer' && (
                        <>
                          <div>
                            <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Province(s)</label>
                            <div className="text-[16px] font-[500] text-[#000000]">
                              {user?.region && user.region.length > 0 
                                ? user.region.map((province: any) => province.name).join(', ')
                                : 'No region(s) assigned'
                              }
                            </div>
                          </div>
                          {/* Hide Local Unions if there are no valid locals or only N/A locals */}
                          {user?.local?.length > 0 && user.local.some((local: any) => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A') && (
                            <div>
                              <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Local Unions</label>
                              <div className="flex flex-wrap gap-2">
                                {user.local
                                  .filter((local: any) => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A')
                                  .map((local: any) => (
                                    <span
                                      key={local.id}
                                      className="bg-gray-300 text-text-primary text-small font-medium px-2.5 py-0.5 rounded-full"
                                    >
                                      {local.code} - Local {local.unionNumber}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <div className="mt-8 space-y-4">
                        <div className="flex items-center gap-4">
                          <label className="block text-[14px] font-[500] text-[#767A81] w-32">Last Login</label>
                          <div className="text-[14px] font-[500] text-[#000000]">{user?.lastLogin}</div>
                        </div>

                        <div className="flex items-center gap-4">
                          <label className="block text-[14px] font-[500] text-[#767A81] w-32">Last Updated</label>
                          <div className="text-[14px] font-[500] text-[#000000]">{user?.updated_at}</div>
                        </div>

                        <div className="flex items-center gap-4">
                          <label className="block text-[14px] font-[500] text-[#767A81] w-32">User Created</label>
                          <div className="text-[14px] font-[500] text-[#000000]">{user?.created_at}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {isEditMode && (
                  <div className="fixed bottom-0 right-0 w-full p-4 bg-white border-t border-border">
                    <button
                      className={`w-full btn-primary text-small ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={handleSaveClick}
                      disabled={!isFormValid}
                      id="save-user-changes-button"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Update Confirmation Dialog */}
      <Dialog
        id="update-user-dialog"
        isOpen={showUpdateConfirmDialog}
        onClose={handleCancelUpdate}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        hideCloseButton={true}
        disableBackdropClick={true}
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white flex items-center justify-center mx-auto mb-4">
              <AlertCircleIcon className="w-16 h-16 text-[#FF2C23]" />
            </div>
          </div>
          <h3 className="text-heading font-heading mb-2">Are you sure?</h3>
          <p className="text-text-secondary mb-8">Are you sure to update the changes?</p>

          <div className="flex justify-center gap-4">
            <button
              id="btn-update-cancel"
              onClick={handleCancelUpdate}
              className="btn-secondary px-8 py-2"
            >
              No
            </button>
            <button
              id="btn-update-confirm"
              onClick={handleConfirmUpdate}
              className="btn-primary px-8 py-2"
            >
              Yes
            </button>
          </div>
        </div>
      </Dialog>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog
        id="unsaved-changes-dialog"
        isOpen={showUnsavedChangesDialog}
        onClose={handleCancelUnsavedChanges}
        width="w-[450px]"
        padding="px-10 py-10"
        className="z-[60]"
        hideCloseButton={true}
        disableBackdropClick={true}
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white flex items-center justify-center mx-auto mb-4">
              <AlertCircleIcon className="w-16 h-16 text-[#FF2C23]" />
            </div>
          </div>
          <h3 className="text-heading font-heading mb-2">Unsaved Changes</h3>
          <p className="text-text-secondary mb-8">You have unsaved changes. If you leave, your changes will be lost.</p>

          <div className="flex justify-center gap-4">
            <button
              id="btn-unsaved-cancel"
              onClick={handleCancelUnsavedChanges}
              className="btn-secondary px-8 py-2"
            >
              Cancel
            </button>
            <button
              id="btn-unsaved-proceed"
              onClick={handleCloseWithoutSaving}
              className="btn-primary px-8 py-2"
            >
              Proceed Without Saving
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default UserDetailsOverlay; 